import { makeAutoObservable, runInAction, computed } from "mobx";
import {
  School,
  Student,
  StudentData,
  ClassDetails,
  AddClassRequest,
  AddStudentsToClassRequest,
  ImportClassResponse,
} from "@shared/types";
import {
  getTeacherData,
  deleteStudent,
  getStudentData,
  updateStudent,
  searchStudents,
  addStudentsToClass,
  addNewClassWithStudents
} from "../../services/api";
import { IRootStore } from "@shared/types";

/**
 * 👨‍🏫 TEACHER CONTROLLER - Controller לניהול נתוני מורים וכיתות
 * 
 * **תפקיד ראשי:** Controller שמנהל את כל המידע והפעולות הקשורות למורים, בתי ספר, כיתות ותלמידים
 * 
 * **אחריות:**
 * - ניהול נתוני מורים מהשרת (בתי ספר, כיתות, תלמידים)
 * - ניהול בחירות נוכחיות (בית ספר נבחר, כיתה נבחרת)
 * - פעולות CRUD על תלמידים (הוספה, עריכה, מחיקה, חיפוש)
 * - ניהול כיתות (יצירה חדשה, הוספת תלמידים מאקסל)
 * - חישוב סטטיסטיקות (סה"כ תלמידים, כיתות)
 * 
 * **מתקשר עם:**
 * - teacherService (API) - לכל הפעולות מול השרת
 * - userStore - לקבלת נתוני המשתמש הנוכחי
 * - UI Components - לתצוגת נתונים וטפסים
 * 
 * **Pattern:** Repository + Controller - מנהל נתונים + API calls + business logic
 */
class TeacherController {
  // State
  rootStore: IRootStore;
  schools: School[] = [];
  selectedSchoolId: number | null = null;
  selectedClassId: number | null = null;
  currentSchool: School | null = null;
  currentStudentData: StudentData | null = null;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  // ---- API Actions ----

  /**
   * טעינת נתוני המורה מהשרת
   * כולל בתי ספר, כיתות ותלמידים
   */
  fetchTeacherData = async () => {
    try {
      const response = await getTeacherData();
      runInAction(() => {
        this.schools = response.schools || [];
        

        if (this.schools.length === 1) {
          this.selectedSchoolId = this.schools[0].id;
          this.currentSchool = this.schools[0];
        } else if (this.schools.length > 1 && !this.currentSchool) {
          this.selectedSchoolId = this.schools[0].id;
          this.currentSchool = this.schools[0];
        }
      });
    } catch (error) {
      console.error("Error in fetchTeacherData:", error);
      throw error;
    }
  };

  /**
   * מחיקת תלמיד
   */
  deleteStudent = async (studentId: number) => {
    await deleteStudent(studentId);
    runInAction(() => {
      this.schools.forEach((school) => {
        school.grades.forEach((classItem) => {
          classItem.students = classItem.students.filter(
            (student) => student.id !== studentId
          );
        });
      });
    });
  };

  /**
   * קבלת נתוני תלמיד ספציפי
   */
  getStudentData = async (studentId: number) => {
    return await getStudentData(studentId);
  };

  /**
   * עדכון נתוני תלמיד
   */
  updateStudent = async (
    studentId: number,
    studentData: Partial<Student>
  ) => {
    const updatedStudent = await updateStudent(
      studentId,
      studentData
    );
    runInAction(() => {
      this.schools.forEach((school) => {
        school.grades.forEach((classItem) => {
          const studentIndex = classItem.students.findIndex(
            (s) => s.id === studentId
          );
          if (studentIndex !== -1) {
            classItem.students[studentIndex] = updatedStudent;
          }
        });
      });
    });
  };

  /**
   * חיפוש תלמידים לפי שאילתה
   */
  searchStudents = async (query: string) => {
    return await searchStudents(query);
  };

  /**
   * הוספת תלמיד יחיד לכיתה
   */
  addSingleStudentToClass = async (classId: number, studentData: Student): Promise<ImportClassResponse> => {
    try {
        const response = await addStudentsToClass({
            classId,
            students: [studentData]
        });
        return response;
    } catch (error) {
        console.error("Error adding single student to class:", error);
        throw error;
    }
  };

  /**
   * הוספת תלמידים לכיתה מקובץ אקסל
   */
  addStudentsToClass = async (classId: number, excelFile: File) => {
    const targetClass = this.allClasses.find(c => c.id === classId);
    if (!targetClass) throw new Error("Class not found");

    const studentsData: AddStudentsToClassRequest = {
      classId,
      schoolId: this.currentSchool?.id ?? this.schools[0]?.id,
      file: excelFile,
      name: targetClass.name
    };

    const response = await addStudentsToClass(studentsData);
    
    runInAction(() => {
      if (response?.teacherData?.schools) {
        this.schools = [...response.teacherData.schools];
        this.currentSchool = this.schools.find(school => school.id === this.currentSchool?.id) ?? null;
      }
    });

    return response;
  };

  /**
   * יצירת כיתה חדשה עם תלמידים
   */
  addNewClassWithStudents = async (classDetails: ClassDetails) => {
    const userStore = this.rootStore.stores?.userStore;
    const schoolFromUser = userStore?.userData?.personalInfo?.school;
    
    const classData: AddClassRequest = {
      schoolId: this.currentSchool?.id ?? schoolFromUser?.id ?? this.schools[0]?.id,
      gradeId: classDetails?.classId,
      name: classDetails?.name,
      students: classDetails?.students,
    };


    const response = await addNewClassWithStudents(classData);
    
    runInAction(() => {
      if (response.teacherData) {
        this.schools = [...response.teacherData.schools];
        
        if (!this.currentSchool && this.schools.length > 0) {
          const targetSchoolId = schoolFromUser?.id ?? this.schools[0]?.id;
          this.currentSchool = this.schools.find(school => school.id === targetSchoolId) ?? this.schools[0];
          this.selectedSchoolId = this.currentSchool.id;
        } else {
          this.currentSchool = this.schools.find(school => school.id === this.currentSchool?.id) ?? null;
        }
      }
    });

    return response;
  };

  // ---- State Setters ----

  /**
   * קביעת הכיתה הנבחרת
   */
  setSelectedClass = (classId: number | null) => {
    runInAction(() => {
      this.selectedClassId = classId;
    });
  };

  /**
   * קביעת בית הספר הנוכחי
   */
  setCurrentSchool = (schoolId: number) => {
    runInAction(() => {
      const school = this.schools.find(s => s.id === schoolId);
      if (school) {
        this.selectedSchoolId = schoolId;
        this.currentSchool = school;
        this.selectedClassId = null;
      }
    });
  };

  // ---- Computed Getters ----

  /**
   * בית הספר הנבחר כרגע
   */
  get selectedSchool(): School | null {
    if (!this.selectedSchoolId) return null;
    return (
      this.schools.find((school) => school.id === this.selectedSchoolId) ?? null
    );
  }

  /**
   * הכיתה הנבחרת כרגע
   */
  get selectedClass() {
    if (!this.selectedClassId) return null;
    return (
      this.currentSchool?.grades.find(
        (classItem) => classItem.id === this.selectedClassId
      ) ?? null
    );
  }

  /**
   * כל הכיתות מכל בתי הספר
   */
  get allClasses() {
    return this.schools.flatMap(school => school.grades);
  }

  /**
   * כל התלמידים מכל הכיתות
   */
  get allStudents() {
    return this.allClasses.flatMap(classItem => classItem.students);
  }

  /**
   * סה"כ תלמידים
   */
  get totalStudents() {
    return this.allStudents.length;
  }

  /**
   * סה"כ כיתות
   */
  get totalClasses() {
    return this.allClasses.length;
  }

  /**
   * כיתות בית הספר הנוכחי
   */
  get currentClasses() {
    return this.currentSchool?.grades ?? [];
  }

  // ---- Utility Methods ----

  /**
   * איפוס מצב הסטור
   */
  reset = () => {
    runInAction(() => {
      this.schools = [];
      this.selectedSchoolId = null;
      this.selectedClassId = null;
      this.currentSchool = null;
      this.currentStudentData = null;
    });
  };
}

export default TeacherController; 